<template>
  <el-dialog title="导入已有路线坐标" :visible="visible" width="1040px" append-to-body :close-on-click-modal="false" :close-on-press-escape="!saving" :show-close="!saving" :before-close="close">
    <div class="coordinates-target">当前路线：<strong>{{ route ? route.routeName : '-' }}</strong></div>
    <el-form label-width="90px" size="small" @submit.native.prevent>
      <el-form-item label="坐标来源">
        <el-select v-model="coordinateSystem" :disabled="saving">
          <el-option label="GPS" value="WGS84" />
          <el-option label="高德／腾讯" value="GCJ02" />
          <el-option label="百度" value="BD09" />
        </el-select>
      </el-form-item>
      <el-form-item label="路线点列">
        <el-input v-model="coordinates" type="textarea" :rows="5" :disabled="saving" :placeholder="'每行一个经纬度，至少2点、最多5000点。例如：\n120.123456,30.123456\n121.123456,31.123456'" />
        <div class="coordinates-help">经度在前、纬度在后，单位为度；整数编码坐标请先按数据来源换算。</div>
      </el-form-item>
    </el-form>
    <el-alert v-if="previewError" :title="previewError" type="error" :closable="false" show-icon class="coordinates-alert" />
    <div v-if="previewResult" class="coordinates-result">
      已预览 {{ previewResult.points.length }} 个点，里程 {{ Number(previewResult.totalMileage).toFixed(2) }} 公里。
      <div v-if="Number(previewResult.referenceCount) > 0" class="coordinates-impact">此路线被 {{ previewResult.referenceCount }} 张运单引用，更新会影响引用运单的规划展示和监控。</div>
      <div v-else class="coordinates-help">当前没有运单引用此路线。确认后更新路线几何、起终点坐标和里程。</div>
    </div>
    <div class="coordinates-legend"><span class="coordinates-line coordinates-line--old" />现有路线<span class="coordinates-line coordinates-line--new" />待导入路线</div>
    <div v-loading="previewLoading" class="coordinates-map-shell">
      <div ref="previewMap" class="coordinates-map" />
      <div v-if="!mapReady" class="coordinates-map-hint">{{ previewError ? '预览未完成，请点击“重新预览”重试。' : '请填写点列并预览路线，地图展示成功后可确认保存。' }}</div>
    </div>
    <span slot="footer">
      <el-button :disabled="saving" @click="close">取消</el-button>
      <el-button :loading="previewLoading" :disabled="saving" @click="preview">{{ previewResult || previewError ? '重新预览' : '预览路线' }}</el-button>
      <el-button type="primary" :loading="saving" :disabled="!canSave" @click="save">确认保存</el-button>
    </span>
  </el-dialog>
</template>

<script>
import { previewRouteCoordinates, saveRouteCoordinates, isRequestSuccess } from './lingdongApi'
import { confirmRoutePassage } from './routePassage'
import { loadAmapSDK } from '@/utils/amapConfig'
import { wgs84RouteToGcj02, wgs84ToGcj02Point } from './coordinate'

export default {
  name: 'RouteCoordinatesDialog',
  props: {
    visible: { type: Boolean, default: false },
    route: { type: Object, default: null }
  },
  data() {
    return {
      coordinateSystem: 'WGS84', coordinates: '', previewResult: null, previewKey: '', previewError: '',
      previewLoading: false, saving: false, mapReady: false, previewRequestId: 0, saveRequestId: 0, map: null
    }
  },
  computed: {
    inputKey() {
      return JSON.stringify([this.route, this.coordinateSystem, this.coordinates])
    },
    canSave() { return this.visible && !this.previewLoading && !this.saving && this.mapReady && !!this.previewResult && this.previewKey === this.inputKey }
  },
  watch: {
    visible(value) {
      if (value) { this.coordinates = ''; this.coordinateSystem = 'WGS84' }
      this.invalidatePreview()
    },
    inputKey() { this.invalidatePreview() }
  },
  beforeDestroy() { this.invalidatePreview() },
  methods: {
    invalidatePreview() {
      this.previewRequestId += 1
      this.saveRequestId += 1
      this.previewResult = null
      this.previewKey = ''
      this.previewError = ''
      this.previewLoading = false
      this.saving = false
      this.mapReady = false
      this.destroyMap()
    },
    destroyMap() {
      if (this.map) { this.map.destroy(); this.map = null }
    },
    close() {
      this.invalidatePreview()
      this.$emit('update:visible', false)
    },
    buildInput() {
      if (!this.route || this.route.id == null || Number(this.route.status) !== 1) throw new Error('请选择正常的已有路线')
      if (!['WGS84', 'GCJ02', 'BD09'].includes(this.coordinateSystem)) throw new Error('请选择坐标来源')
      const lines = this.coordinates.split(/\r?\n/).map((line, index) => ({ line: line.trim(), index })).filter(item => item.line)
      if (lines.length < 2 || lines.length > 5000) throw new Error('路线点数必须为2至5000个')
      const points = lines.map(({ line, index }) => {
        const parts = line.split(',').map(value => value.trim())
        const values = parts.map(Number)
        if (parts.length !== 2 || parts.some(value => !value) || !values.every(Number.isFinite) || Math.abs(values[0]) > 180 || Math.abs(values[1]) > 90) {
          throw new Error(`第${index + 1}行坐标无效，请填写经度,纬度，单位为度`)
        }
        return { longitude: values[0], latitude: values[1] }
      })
      return { routeId: String(this.route.id), coordinateSystem: this.coordinateSystem, points }
    },
    async preview() {
      if (this.saving) return
      this.invalidatePreview()
      const requestId = ++this.previewRequestId
      const key = this.inputKey
      const isCurrent = () => this.visible && requestId === this.previewRequestId && key === this.inputKey
      this.previewLoading = true
      try {
        const response = await previewRouteCoordinates(this.buildInput())
        if (!isCurrent()) return
        if (!isRequestSuccess(response)) throw new Error(response.msg || '路线预览失败')
        const result = response.data
        if (!result || result.coordinateSystem !== 'WGS84' || !Array.isArray(result.points) || result.points.length < 2
            || !result.expectedRouteHash || !Number.isFinite(Number(result.totalMileage)) || Number(result.totalMileage) < 0
            || !Number.isInteger(Number(result.referenceCount)) || Number(result.referenceCount) < 0) throw new Error('路线预览结果不完整，请重试')
        this.previewResult = result
        this.previewKey = key
        await this.$nextTick()
        if (!isCurrent()) return
        await this.drawPreview(result, isCurrent)
        if (isCurrent()) this.mapReady = true
      } catch (error) {
        if (!isCurrent()) return
        this.mapReady = false
        this.previewError = error.msg || error.message || '路线预览失败，请重试'
        this.destroyMap()
        this.$message.error(this.previewError)
      } finally {
        if (isCurrent()) this.previewLoading = false
      }
    },
    async drawPreview(result, isCurrent) {
      const sdk = await loadAmapSDK()
      if (!isCurrent()) return
      if (!this.$refs.previewMap) throw new Error('地图容器尚未就绪，请重新预览')
      const candidate = result.points.map(point => wgs84ToGcj02Point([Number(point.longitude), Number(point.latitude)]))
      if (candidate.some(point => !point || !point.every(Number.isFinite))) throw new Error('待导入路线坐标无效')
      this.destroyMap()
      const map = new sdk.Map(this.$refs.previewMap, { zoom: 8, center: candidate[0] })
      this.map = map
      const original = wgs84RouteToGcj02(this.route.routeList)
      const overlays = []
      if (original.length > 1) overlays.push(new sdk.Polyline({ path: original, strokeColor: '#8c8c8c', strokeWeight: 5, strokeOpacity: 0.65, zIndex: 10 }))
      overlays.push(new sdk.Polyline({ path: candidate, strokeColor: '#1677ff', strokeWeight: 6, zIndex: 20 }))
      overlays.push(new sdk.Marker({ position: candidate[0], label: { content: '导入起点', direction: 'top' } }))
      overlays.push(new sdk.Marker({ position: candidate[candidate.length - 1], label: { content: '导入终点', direction: 'top' } }))
      map.add(overlays)
      map.setFitView()
    },
    passageNodes() {
      const route = this.route
      let waypoints
      try { waypoints = JSON.parse(route.waypoints || '[]'); if (!Array.isArray(waypoints)) throw new Error() } catch (error) {
        waypoints = [{ address: '途经点配置无效，无法判断', lonLat: '' }]
      }
      return [
        { code: 'start', name: `起点：${route.startAddress || ''}`, lonLat: route.startLonlat, radius: route.startRadius },
        ...waypoints.map((node, index) => ({ code: `waypoint-${index + 1}`, name: `途经点 ${index + 1}：${node.address || ''}`, lonLat: node.lonLat, radius: node.radius })),
        { code: 'end', name: `终点：${route.endAddress || ''}`, lonLat: route.endLonlat, radius: route.endRadius }
      ]
    },
    async save() {
      if (this.saving) return
      if (!this.canSave) { this.$message.warning('请先成功预览当前输入的路线'); return }
      // 首次异步前锁定提交，提醒和保存共用同一份原始输入。
      this.saving = true
      const requestId = ++this.saveRequestId
      const key = this.inputKey
      const isCurrent = () => this.visible && requestId === this.saveRequestId && key === this.inputKey
      try {
        const payload = { ...this.buildInput(), expectedRouteHash: this.previewResult.expectedRouteHash }
        const routeList = this.previewResult.points.map(point => `${point.longitude},${point.latitude}`).join('|')
        if (!await confirmRoutePassage(this, { routeList, nodes: this.passageNodes(), fenceCodes: [] }, isCurrent) || !isCurrent()) return
        const response = await saveRouteCoordinates(payload)
        if (!isCurrent()) return
        if (!isRequestSuccess(response)) throw new Error(response.msg || '路线保存失败')
        this.$message.success('路线坐标已更新')
        this.$emit('saved')
        this.close()
      } catch (error) {
        if (!isCurrent()) return
        this.previewKey = ''
        this.mapReady = false
        this.previewError = error.msg || error.message || '保存失败，请重新预览后重试'
        this.$message.error(this.previewError)
      } finally {
        if (isCurrent()) this.saving = false
      }
    }
  }
}
</script>

<style scoped>
.coordinates-target { margin-bottom: 16px; }
.coordinates-help { color: #909399; font-size: 12px; line-height: 22px; }
.coordinates-alert { margin-bottom: 12px; }
.coordinates-result { margin-bottom: 12px; line-height: 24px; }
.coordinates-impact { color: #b76e00; }
.coordinates-legend { display: flex; align-items: center; gap: 8px; margin-bottom: 8px; color: #606266; font-size: 12px; }
.coordinates-line { display: inline-block; width: 24px; height: 4px; margin-left: 12px; }
.coordinates-line--old { background: #8c8c8c; }
.coordinates-line--new { background: #1677ff; }
.coordinates-map-shell { position: relative; }
.coordinates-map { width: 100%; height: 340px; border: 1px solid #ebeef5; }
.coordinates-map-hint { position: absolute; left: 0; top: 0; right: 0; padding: 40px 20px; text-align: center; color: #909399; }
</style>
